import React from 'react';
import { useLanguage, type Language } from './LanguageContext';
import { getTranslation, formatTranslation } from './translations';

// useTranslate Hook的返回值类型
export interface UseTranslateReturn {
  t: (key: string, params?: Record<string, string | number>) => string;
  language: Language;
  setLanguage: (language: Language) => void;
  supportedLanguages: Array<{
    code: Language;
    name: string;
    nativeName: string;
  }>;
}

/**
 * 自定义Hook用于页面翻译
 * @returns 包含翻译函数和语言相关信息的对象
 */
export const useTranslate = (): UseTranslateReturn => {
  const { language, setLanguage, supportedLanguages } = useLanguage();

  /**
   * 翻译函数
   * @param key - 翻译键名
   * @param params - 可选的插值参数
   * @returns 翻译后的文本
   */
  const t = (key: string, params?: Record<string, string | number>): string => {
    if (params) {
      return formatTranslation(key, language, params);
    }
    return getTranslation(key, language);
  };

  return {
    t,
    language,
    setLanguage,
    supportedLanguages,
  };
};

/**
 * 高阶组件：为组件提供翻译功能
 * @param Component - 需要包装的组件
 * @returns 包装后的组件
 */
export function withTranslation<P extends object>(
  Component: React.ComponentType<P & { t: (key: string, params?: Record<string, string | number>) => string }>
): React.FC<P> {
  const WrappedComponent: React.FC<P> = (props) => {
    const { t } = useTranslate();
    const ComponentWithT = Component as React.ComponentType<any>;
    return React.createElement(ComponentWithT, { ...props, t });
  };
  
  WrappedComponent.displayName = `withTranslation(${Component.displayName || Component.name})`;
  return WrappedComponent;
}

export default useTranslate;